import { groupBy } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import useAppState from '../../state/appState';
import { SummaryPage as SummaryPageType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { trackSummaryPageShown, trackABHighwaySplitTestSummary } from '../../utils/tracking';
import { isElementVisible, isHiddenElement } from '../../conditional-fields/evaluate-field-condition';
import { LoadingDots, Table } from '@flos/react-ui';
import { getResumeSectionFromElement, getValue } from '../values/Get';
import { getArrayOfPagesShown } from '../../helpers/getPageInfo';
import { SubmitContext } from '../../context/SubmitContext';

const SummaryGroup = ({
    caption,
    summaryGroupId,
    formValues,
    formConfig,
    pagesShown
}: {
    caption: string;
    summaryGroupId: string;
    formValues: FormValues;
    formConfig: FormConfig;
    pagesShown: string[];
}) => {
    if (!Object.keys(formValues).length) {
        return <></>;
    }

    // This take only elements from form config for this summary group
    // AND the page is in the pagesShown array
    const elements = formConfig.pages
        .filter((page) => pagesShown.includes(page.pageId))
        .flatMap((page) => page.elements)
        .filter((element) => element.summaryGroupId === summaryGroupId);

    // Prevent showing pages that were not shown to the user
    if (!elements.length) {
        return <React.Fragment key={'no-group'} />;
    }

    const elementsBySummaryGroupId = groupBy(elements, (element) => element.summaryGroupId);

    return (
        <div className="summary-page-group" key={`box-item-group-${summaryGroupId}`}>
            <h4>{caption}</h4>
            <Table wrapped mobileDirection="vertical">
                <Table.Body>
                    {Object.entries(elementsBySummaryGroupId)
                        .filter(([lookupSummaryGroupId]) => lookupSummaryGroupId === summaryGroupId)
                        .flatMap(([_, elements]) => elements)
                        .map((element, i) => {
                            const { elementId } = element;
                            const fieldIsVisible =
                                isElementVisible(formConfig, formValues, elementId) &&
                                !isHiddenElement(formConfig, elementId);
                            const formValue = getValue(formConfig, formValues, elementId);
                            if (!fieldIsVisible) {
                                return <React.Fragment key={'summaryElement-' + i} />;
                            }

                            if (!formValue) {
                                return <React.Fragment key={'summaryElement-' + i} />;
                            }

                            return (
                                <React.Fragment key={'summaryElement-' + i}>
                                    {getResumeSectionFromElement(formConfig, formValues, elementId)}
                                </React.Fragment>
                            );
                        })}
                </Table.Body>
            </Table>
        </div>
    );
};

const SummaryPage = ({ caption }: SummaryPageType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setHasReachedSummary = useAppState((state) => state.setHasReachedSummary);
    const { isRedirecting } = useContext(SubmitContext);

    const [pagesShown, setPagesShown] = useState<string[]>([]);

    if (!formConfig) {
        throw new Error('This component must be an outlet of <Form/>');
    }

    if (!formValues) {
        throw new Error('Could not get the values from the state for the form');
    }

    useEffect(() => {
        setHasReachedSummary(true);
        trackSummaryPageShown(formConfig);
        trackABHighwaySplitTestSummary(formConfig);
        // eslint-disable-next-line
    }, [formConfig]);

    useEffect(() => {
        getArrayOfPagesShown(formConfig, formValues).then((pageArray) => {
            setPagesShown(pageArray);
        });
    }, [formConfig, formValues]);
    return isRedirecting ? (
        <LoadingDots centered={false} />
    ) : (
        <div className="summary-page">
            {caption && <h2>{caption}</h2>}
            {formConfig.summaryGroups.map(({ summaryGroupId, caption }, i) => {
                return (
                    <React.Fragment key={'sg-' + i}>
                        <SummaryGroup
                            caption={caption}
                            summaryGroupId={summaryGroupId}
                            formValues={formValues}
                            formConfig={formConfig}
                            pagesShown={pagesShown}
                        />
                    </React.Fragment>
                );
            })}
        </div>
    );
};

SummaryPage.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return true;
};

export default SummaryPage;
